/**
 * Screenshot capture utilities for presentation slides.
 * Uses html-to-image library to convert preview slides to PNG images.
 */

import { toPng, toBlob } from 'html-to-image';

export interface SlideImage {
  slideNumber: number;
  dataUrl: string;
  blob: Blob;
}

export interface CaptureOptions {
  /** Quality of the output image (0.0 to 1.0). Default: 0.95 */
  quality?: number;
  /** Pixel ratio for high DPI displays. Default: 2 */
  pixelRatio?: number;
  /** Background color to use if element has transparent background */
  backgroundColor?: string;
}

const DEFAULT_OPTIONS: Required<CaptureOptions> = {
  quality: 0.95,
  pixelRatio: 2,
  backgroundColor: '#ffffff',
};

/**
 * Capture all slides from a preview container as PNG images.
 *
 * @param containerElement - The container element holding the preview slides
 * @param options - Capture options (quality, pixelRatio, backgroundColor)
 * @returns Array of captured slide images with data URLs and blobs
 */
export async function captureSlides(
  containerElement: HTMLElement,
  options: CaptureOptions = {}
): Promise<SlideImage[]> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  console.log(`[PresentationCapture] captureSlides called — container: tag=${containerElement.tagName}, class="${containerElement.className}", ${containerElement.offsetWidth}x${containerElement.offsetHeight}, children=${containerElement.childElementCount}`);

  // Find all slide elements in the preview container
  const slideSelectors = [
    '.pptx-preview-slide-wrapper', // pptx-preview library
    '.slide',          // Common slide class
    '[data-slide]',    // Data attribute pattern
  ];

  let slideElements: NodeListOf<HTMLElement> | HTMLElement[] =
    containerElement.querySelectorAll(slideSelectors.join(', '));

  console.log(`[PresentationCapture] Primary selectors "${slideSelectors.join(', ')}" matched ${slideElements.length} element(s)`);

  // If no specific slide elements found, try pptx-preview wrapper children
  if (slideElements.length === 0) {
    const wrapper = containerElement.querySelector('.pptx-preview-wrapper');
    console.log(`[PresentationCapture] Fallback: .pptx-preview-wrapper ${wrapper ? 'FOUND' : 'NOT FOUND'}`);
    if (wrapper) {
      const slides = Array.from(wrapper.children).filter(
        (el) => el.classList.contains('pptx-preview-slide-wrapper')
      ) as HTMLElement[];
      console.log(`[PresentationCapture] Fallback: filtered ${slides.length} .pptx-preview-slide-wrapper children from wrapper`);
      if (slides.length > 0) {
        slideElements = slides;
      }
    }
  }

  // Still nothing? Capture the whole container as one "slide"
  if (slideElements.length === 0) {
    console.warn(`[PresentationCapture] No individual slides found via any selector — falling back to entire container (${containerElement.offsetWidth}x${containerElement.offsetHeight})`);
    slideElements = [containerElement];
  }

  console.log(`[PresentationCapture] Will capture ${slideElements.length} slide element(s)`);

  const images: SlideImage[] = [];

  for (let i = 0; i < slideElements.length; i++) {
    const slideEl = slideElements[i] as HTMLElement;
    console.log(`[PresentationCapture] Capturing slide ${i + 1}/${slideElements.length} — tag=${slideEl.tagName}, class="${slideEl.className}", ${slideEl.offsetWidth}x${slideEl.offsetHeight}`);

    try {
      // Capture as PNG data URL
      const dataUrl = await toPng(slideEl, {
        quality: opts.quality,
        pixelRatio: opts.pixelRatio,
        backgroundColor: opts.backgroundColor,
        // Skip elements that might cause issues
        filter: (node: HTMLElement) => {
          // Skip script and style tags
          if (node.tagName === 'SCRIPT' || node.tagName === 'STYLE') {
            return false;
          }
          return true;
        },
      });

      console.log(`[PresentationCapture] toPng succeeded for slide ${i + 1} — dataUrl length: ${dataUrl.length}`);

      // Also get as blob for potential download/upload
      const blob = await toBlob(slideEl, {
        quality: opts.quality,
        pixelRatio: opts.pixelRatio,
        backgroundColor: opts.backgroundColor,
      });

      if (blob) {
        console.log(`[PresentationCapture] toBlob succeeded for slide ${i + 1} — blob size: ${blob.size} bytes`);
        images.push({
          slideNumber: i + 1,
          dataUrl,
          blob,
        });
      } else {
        console.warn(`[PresentationCapture] toBlob returned null for slide ${i + 1}`);
      }
    } catch (error) {
      console.error(`[PresentationCapture] Failed to capture slide ${i + 1}:`, error);
      // Continue with other slides even if one fails
    }
  }

  console.log(`[PresentationCapture] captureSlides complete — ${images.length}/${slideElements.length} slides captured successfully`);
  return images;
}

/**
 * Capture a single element as a PNG image.
 *
 * @param element - The HTML element to capture
 * @param options - Capture options
 * @returns Object with dataUrl and blob
 */
export async function captureElement(
  element: HTMLElement,
  options: CaptureOptions = {}
): Promise<{ dataUrl: string; blob: Blob } | null> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  try {
    const dataUrl = await toPng(element, {
      quality: opts.quality,
      pixelRatio: opts.pixelRatio,
      backgroundColor: opts.backgroundColor,
    });

    const blob = await toBlob(element, {
      quality: opts.quality,
      pixelRatio: opts.pixelRatio,
      backgroundColor: opts.backgroundColor,
    });

    if (blob) {
      return { dataUrl, blob };
    }
    return null;
  } catch (error) {
    console.error('Failed to capture element:', error);
    return null;
  }
}

/**
 * Download captured images as individual PNG files.
 *
 * @param images - Array of captured slide images
 * @param baseFilename - Base filename for downloads (e.g., "presentation")
 */
export function downloadSlideImages(images: SlideImage[], baseFilename: string = 'slide'): void {
  images.forEach((img) => {
    const link = document.createElement('a');
    link.href = img.dataUrl;
    link.download = `${baseFilename}-${img.slideNumber}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  });
}

// ============================================================================
// Thumbnail Grid Generation
// ============================================================================

export interface ThumbnailGridOptions {
  /** Number of columns in the grid. Default: 3 */
  columns?: number;
  /** Width of each thumbnail in pixels. Default: 300 */
  thumbnailWidth?: number;
  /** Padding between thumbnails in pixels. Default: 20 */
  padding?: number;
  /** Border width around thumbnails. Default: 2 */
  borderWidth?: number;
  /** Border color. Default: '#cccccc' */
  borderColor?: string;
  /** Background color of the grid. Default: '#ffffff' */
  backgroundColor?: string;
  /** Show slide labels. Default: true */
  showLabels?: boolean;
  /** Font size for labels. Default: auto (10% of thumbnail width) */
  fontSize?: number;
  /** Label color. Default: '#333333' */
  labelColor?: string;
}

const DEFAULT_GRID_OPTIONS: Required<ThumbnailGridOptions> = {
  columns: 3,
  thumbnailWidth: 300,
  padding: 20,
  borderWidth: 2,
  borderColor: '#cccccc',
  backgroundColor: '#ffffff',
  showLabels: true,
  fontSize: 0, // 0 means auto-calculate
  labelColor: '#333333',
};

export interface ThumbnailGrid {
  dataUrl: string;
  blob: Blob;
  width: number;
  height: number;
  slideCount: number;
}

/**
 * Create a thumbnail grid from captured slide images.
 * Combines all slides into a single image arranged in a grid layout.
 *
 * Similar to thumbnail.py but runs entirely in the browser using canvas.
 *
 * @param images - Array of captured slide images
 * @param options - Grid layout options
 * @returns Promise with the combined grid image
 */
export async function createThumbnailGrid(
  images: SlideImage[],
  options: ThumbnailGridOptions = {}
): Promise<ThumbnailGrid | null> {
  if (images.length === 0) {
    return null;
  }

  const opts = { ...DEFAULT_GRID_OPTIONS, ...options };
  const fontSize = opts.fontSize || Math.round(opts.thumbnailWidth * 0.1);
  const labelHeight = opts.showLabels ? fontSize + opts.padding : 0;

  // Load first image to get aspect ratio
  const firstImg = await loadImage(images[0].dataUrl);
  const aspectRatio = firstImg.height / firstImg.width;
  const thumbnailHeight = Math.round(opts.thumbnailWidth * aspectRatio);

  // Calculate grid dimensions
  const cols = Math.min(opts.columns, images.length);
  const rows = Math.ceil(images.length / cols);

  const cellWidth = opts.thumbnailWidth + opts.padding;
  const cellHeight = thumbnailHeight + labelHeight + opts.padding;

  const gridWidth = cols * cellWidth + opts.padding;
  const gridHeight = rows * cellHeight + opts.padding;

  // Create canvas
  const canvas = document.createElement('canvas');
  canvas.width = gridWidth;
  canvas.height = gridHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // Fill background
  ctx.fillStyle = opts.backgroundColor;
  ctx.fillRect(0, 0, gridWidth, gridHeight);

  // Set up font for labels
  ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
  ctx.fillStyle = opts.labelColor;
  ctx.textAlign = 'center';

  // Draw each slide
  for (let i = 0; i < images.length; i++) {
    const img = await loadImage(images[i].dataUrl);

    const col = i % cols;
    const row = Math.floor(i / cols);

    const x = opts.padding + col * cellWidth;
    const y = opts.padding + row * cellHeight;

    // Draw label above thumbnail
    if (opts.showLabels) {
      const labelX = x + opts.thumbnailWidth / 2;
      const labelY = y + fontSize;
      ctx.fillStyle = opts.labelColor;
      ctx.fillText(`Slide ${images[i].slideNumber}`, labelX, labelY);
    }

    // Calculate thumbnail position (below label)
    const thumbX = x;
    const thumbY = y + labelHeight;

    // Draw border
    if (opts.borderWidth > 0) {
      ctx.strokeStyle = opts.borderColor;
      ctx.lineWidth = opts.borderWidth;
      ctx.strokeRect(
        thumbX - opts.borderWidth / 2,
        thumbY - opts.borderWidth / 2,
        opts.thumbnailWidth + opts.borderWidth,
        thumbnailHeight + opts.borderWidth
      );
    }

    // Draw thumbnail
    ctx.drawImage(img, thumbX, thumbY, opts.thumbnailWidth, thumbnailHeight);
  }

  // Convert canvas to data URL and blob
  const dataUrl = canvas.toDataURL('image/png');
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => {
      if (b) resolve(b);
      else reject(new Error('Failed to create blob'));
    }, 'image/png');
  });

  return {
    dataUrl,
    blob,
    width: gridWidth,
    height: gridHeight,
    slideCount: images.length,
  };
}

/**
 * Helper to load an image from a data URL
 */
function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
}

/**
 * Download a thumbnail grid as a PNG file.
 */
export function downloadThumbnailGrid(grid: ThumbnailGrid, filename: string = 'thumbnails'): void {
  const link = document.createElement('a');
  link.href = grid.dataUrl;
  link.download = `${filename}.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
